import {
  CursorClickIcon,
  KeyboardIcon,
  TextTIcon,
  ScrollIcon,
  CameraIcon,
  GlobeIcon,
  MouseIcon,
  LightningIcon,
  FilmSlateIcon,
  PencilSimpleIcon,
  TerminalIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { useGT } from 'gt-react';

/** Mock UI Studio coding-agent tools — surfaced live by the ai-service progress hook. Their
 * `action` ids are the raw tool names, which the browser-operator keyword matching below doesn't
 * cover, so each is special-cased first in the three helpers. Exported so the chat mapper +
 * screen-group panel key off the same names. */
export const NEXT_SCREEN = 'next_screen';
export const SCREENSHOT_STEP = 'screenshot_step';
export const FILE_EDITOR = 'sandbox_file_editor';
export const BASH = 'sandbox_bash';

/** Basename of a posix path — file edits carry an absolute sandbox path, but a chip reads
 * better as just the file. */
function baseName(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const slash = trimmed.lastIndexOf('/');
  return slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
}

/**
 * The salient parameter of an action, surfaced as an at-a-glance detail — e.g.
 * the key(s) pressed, the typed text, the scroll direction/amount, or the click
 * coordinate. Reads the (already-redacted) action metadata. Null when there's
 * nothing useful to show.
 */
export function actionDetail(
  action: string | undefined,
  metadata: Record<string, unknown> | undefined,
): string | null {
  if (!action || !metadata) return null;
  const a = action.toLowerCase();
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v.trim() : null;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;

  // Mock UI Studio tools first (their metadata carries screen/attempt/path/command).
  if (a.includes(NEXT_SCREEN)) {
    const screen = num(metadata.screen);
    const total = num(metadata.total);
    return screen !== null && total !== null
      ? `${screen + 1} of ${total}`
      : null;
  }
  if (a.includes(SCREENSHOT_STEP)) {
    const attempt = num(metadata.attempt);
    return attempt && attempt > 0 ? `#${attempt}` : null;
  }
  if (a.includes(FILE_EDITOR)) {
    const path = str(metadata.path);
    return path ? baseName(path) : null;
  }
  if (a.includes(BASH)) return str(metadata.command);

  if (a.includes('key')) return str(metadata.text);
  if (a.includes('type') || a.includes('text')) {
    const t = str(metadata.text);
    return t ? `“${t}”` : null;
  }
  if (a.includes('scroll')) {
    const dir = str(metadata.scroll_direction);
    const amt =
      typeof metadata.scroll_amount === 'number'
        ? `×${metadata.scroll_amount}`
        : null;
    return [dir, amt].filter(Boolean).join(' ') || null;
  }
  const c = metadata.coordinate;
  if (
    Array.isArray(c) &&
    c.length === 2 &&
    typeof c[0] === 'number' &&
    typeof c[1] === 'number'
  ) {
    return `(${c[0]}, ${c[1]})`;
  }
  return null;
}

/**
 * The icon for an action we RECOGNIZE, or null when nothing matched.
 *
 * Separate from `actionIcon` because a caller that already has a meaningful default — the span
 * tree, whose tool rows are wrenches and whose subagent rows are robots — must be able to tell
 * "this is a left click" from "this is some tool I don't have a glyph for". Collapsing both onto
 * the lightning bolt would relabel every ordinary tool call in the trace.
 */
export function specificActionIcon(action: string): Icon | null {
  const a = action.toLowerCase();
  // Mock UI Studio tools first (screenshot_step still falls to CameraIcon below).
  if (a.includes(NEXT_SCREEN)) return FilmSlateIcon;
  if (a.includes(FILE_EDITOR)) return PencilSimpleIcon;
  if (a.includes(BASH)) return TerminalIcon;
  if (a.includes('click') || a.includes('press') || a.includes('tap'))
    return CursorClickIcon;
  if (a.includes('type') || a.includes('text')) return TextTIcon;
  if (a.includes('key')) return KeyboardIcon;
  if (a.includes('scroll')) return ScrollIcon;
  if (a.includes('screenshot') || a.includes('capture')) return CameraIcon;
  if (a.includes('navigate') || a.includes('goto') || a.includes('url'))
    return GlobeIcon;
  if (a.includes('move') || a.includes('drag') || a.includes('mouse'))
    return MouseIcon;
  return null;
}

/** Pick an icon for an agent action so the thread reads at a glance. */
export function actionIcon(action: string): Icon {
  return specificActionIcon(action) ?? LightningIcon;
}

export function entryTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** A short, verb-phrased label for an action row in a checklist (e.g. the operator
 * interview's turn cards) — distinct from `formatActionName`'s noun-style Title Case
 * (AgentSessionViewer.tsx), which reads better as a standalone label than as "did X". */
export function turnActionLabel(
  action: string,
  gt: ReturnType<typeof useGT>,
): string {
  const a = action.toLowerCase();
  // Mock UI Studio tools first — the browser-operator verbs below don't fit a coding agent.
  if (a.includes(NEXT_SCREEN))
    return gt('Revealed screen', { context: 'mock UI build action verb' });
  if (a.includes(SCREENSHOT_STEP))
    return gt('Captured render', { context: 'mock UI build action verb' });
  if (a.includes(FILE_EDITOR))
    return gt('Edited', { context: 'mock UI build action verb' });
  if (a.includes(BASH))
    return gt('Ran', { context: 'mock UI build action verb' });
  if (a.includes('click') || a.includes('press') || a.includes('tap'))
    return gt('Clicked', { context: 'operator turn card action verb' });
  if (a.includes('type') || a.includes('text'))
    return gt('Typed', { context: 'operator turn card action verb' });
  if (a.includes('key'))
    return gt('Pressed a key', { context: 'operator turn card action verb' });
  if (a.includes('scroll'))
    return gt('Scrolled', { context: 'operator turn card action verb' });
  if (a.includes('screenshot') || a.includes('capture'))
    return gt('Captured a screenshot', {
      context: 'operator turn card action verb',
    });
  if (a.includes('navigate') || a.includes('goto') || a.includes('url'))
    return gt('Navigated', { context: 'operator turn card action verb' });
  if (a.includes('move') || a.includes('drag') || a.includes('mouse'))
    return gt('Moved the mouse', { context: 'operator turn card action verb' });
  return gt('Acted', {
    context: 'operator turn card action verb, generic fallback',
  });
}
